import * as React from "react";
import { useFormApi, FormState } from "informedv4";
import { Link } from "react-router-dom";
import { Country, Role, TimeZone, UserCreation, WebPortalClient } from "webportalclient";
import { useSecurityContext } from "../../Core/SecurityContext";
import { MultiSelect } from "../../forms/informedv4/MultiSelectV4";
import { TextBox } from "../../forms/informedv4/TextBoxV4";
import { Required } from "../../forms/validation/Required";
import { PermissionEnum } from "../../misc/api/ApiPermissionEnum";
import Alert, { AlertCategoryEnum } from "../../misc/components/Alert";
import { HasGlobalPermission } from "../../misc/components/Authorization";
import { Dropdown } from "../../forms/informedv4/DropdownV4";
import { Checkbox } from "../../forms/informedv4/CheckboxV4";
import { EmailValidator } from "../../forms/validation/EmailValidator";
import LoadingWithMessage from "../../misc/components/LoadingWithMessage";
import { AuditForm, AuditModal } from "../../auditmodal/components/AuditModal";
import { GetCountOfObjectKeys, GroupBy, IsArrayNullOrEmpty } from "../../misc/Helpers/ArrayHelper";
import { DeviceTypes } from "../../misc/api/apiTypes";
import { UserMutation } from "webportalclient";
import AsyncActionButton from "../../misc/components/AsyncActionButton";
import { useReferenceContextProvider } from "../../Core/ReferenceContextProvider";
import { useWebPortalApiClient } from "../../misc/api/WebPortalTokenClient";
import { Site } from "webportalclient";
import { MapToRoleOptions, MapToSiteOptions } from "../../misc/OptionFormatters";

export interface UserFormProps {
    header: string;
    userId?: string;
    languageOptions: { code: string, name: string }[];
    timeZoneOptions: TimeZone[];
}

export interface UserFormState {
    email: string;
    firstName: string;
    lastName: string;
    roles: any[];
    sites: any[];
    language: { code: string, name: string };
    timeZone: TimeZone;
    country: Country;
    spirotracUserGroup: string;
    registeredForMobileApp: boolean;
    isDisabled: boolean;
}

export const UserForm = ({ header, userId, languageOptions, timeZoneOptions }: UserFormProps) => {
    const [loading, setLoading] = React.useState(false);
    const [submitting, setSubmitting] = React.useState(false);
    const [successAlert, setSuccessAlert] = React.useState(false);
    const [errorAlert, setErrorAlert] = React.useState<string>(null);
    const [roles, setRoles] = React.useState([]);
    const [registeredEmails, setRegisteredEmails] = React.useState([]);
    const [roleOptions, setRoleOptions] = React.useState([]);
    const [siteOptions, setSiteOptions] = React.useState([]);
    const [spirotracUserGroupOptions, setSpirotracUserGroupOptions] = React.useState([]);
    const [auditModalVisible, setAuditModalVisible] = React.useState(false);
    const client = useWebPortalApiClient();
    const { currentUser, currentUserPermissions } = useSecurityContext();
    const { countries } = useReferenceContextProvider();
    const formApi = useFormApi();
    const isNew = userId === undefined;

    React.useEffect(() => {
        const fetchData = async () => {
            const mapToOptions = (results: { id?: string; name?: string }[]) => results.map(v => ({ label: v.name, value: v.id }));

            const getAssignableRoles = (roles) => roles.filter(r => r.name !== "Device Hub Client" && r.name !== "Gateway Client");

            const fetchRegisteredUsers = async () => {
                const users = await client.client.getUsers("id,email");
                setRegisteredEmails(users.value);
            }

            const fetchSiteOptions = async () => {
                const sites = await client.client.getSites("id,number", undefined, "number", "study($select=name)");
                setSiteOptions(MapToSiteOptions(sites.value));
            }

            const fetchRoleOptions = async () => {
                const rolesResults = await client.client.getRoles(undefined, undefined, "name", "study");
                setRoles(rolesResults.value);

                if (HasGlobalPermission(currentUserPermissions, PermissionEnum.AssignAllRoles)) {
                    setRoleOptions(MapToRoleOptions(getAssignableRoles(rolesResults.value)));
                } else {
                    const childRoleIds = currentUser.Roles.flatMap(r => r.ChildRolesIds);
                    const childRoles = [];
                    for (const roleId of childRoleIds) {
                        childRoles.push(rolesResults.value.find(r => r.id === roleId));
                    }
                    setRoleOptions(MapToRoleOptions(getAssignableRoles(childRoles)));
                }
            }

            const fetchSpirotracUserGroupOptions = async () => {
                const spirotracUserGroups = await client.client.getSpirotracUserGroups("id,name", undefined);
                setSpirotracUserGroupOptions(mapToOptions(spirotracUserGroups.value));
            }

            setLoading(true);
            await fetchRegisteredUsers();
            await fetchRoleOptions();
            await fetchSiteOptions();
            await fetchSpirotracUserGroupOptions();
            setLoading(false);
        }

        fetchData();
    }, []);

    const getSelectedApiRoles = (selectedRoles: any[]) => roles.filter(r => selectedRoles.some(v => v.value === r.id));

    const isPermissionRequired = (selectedRoles: any[], permission: PermissionEnum) => {
        return getSelectedApiRoles(selectedRoles).some(r => r.permissionsIds.includes(permission));
    }

    const isSelectedStudyConfiguredForMobile = (groupedRolesStudies) => {
        Object.keys(groupedRolesStudies).forEach((key, _) => {
            const study = groupedRolesStudies[key];
            study.forEach((_, index) => {
                const role = study[index];
                const deviceTypes = role.study.deviceTypeIds;
                const configured = deviceTypes.includes(DeviceTypes.MobileApp);
                if (configured)
                    return true;
            })
        });

        return false;
    }

    const validateEmail = (value: string) => {
        const requiredMessage = Required(value);
        if (requiredMessage)
            return requiredMessage;

        const invalidMessage = EmailValidator(value);
        if (invalidMessage)
            return invalidMessage;

        if (registeredEmails.filter(e => e.id === userId).includes(value))
            return "Another user is already registered with this email address";

        return undefined;
    }

    const validateRoles = (value: any, values: any) => {
        if (IsArrayNullOrEmpty(value))
            return "Field is required";

        if (values.registeredForMobileApp) {
            const selectedRoles = getSelectedApiRoles(value);
            const rolesGroupedByStudy = GroupBy(selectedRoles, "studyId");

            if ('null' in rolesGroupedByStudy)
                return "A role must be related to a study for mobile application registration";

            if (!isSelectedStudyConfiguredForMobile(rolesGroupedByStudy))
                return "The selected study is not configured to use the mobile app";

            const groupedRolesCount = GetCountOfObjectKeys(rolesGroupedByStudy);
            if (groupedRolesCount !== 1)
                return "A mobile app user must only be a member of a single study";

            if (values.spirotracUserGroup)
                return "A mobile app user cannot be a member of a spirotrac user group";
        }

        return undefined;
    }

    const validateSites = (value: any, values: any) => {
        if (!value || value.length === 0) {
            if (!IsArrayNullOrEmpty(values.roles)) {
                const siteRequired = isPermissionRequired(values.roles, PermissionEnum.RequiresSite);
                if (values.roles.map(r => r.value).includes(roles.find(x => x.name === "Administrator").id)) {
                    return undefined;
                }
                if (siteRequired)
                    return "A site selection is required for this user's role selections";
            }
            if (values.registeredForMobileApp)
                return "A mobile app user must be associated with a single site";
        }
    }

    const validateSpirotracUserGroup = (value: string, values: Record<string,unknown>) => {
        if ((value && !(value == "NONE")) && values.registeredForMobileApp)
            return "A mobile app user cannot be a member of a spirotrac user group";
    }

    const validateForm = () => {
        formApi.validate();
        const formState = formApi.getFormState();
        return formState.errors && Object.keys(formState.errors).length === 0 && Object.getPrototypeOf(formState.errors) === Object.prototype;
    }

    const onPreAuditSubmit = () => {
        setSubmitting(true);

        const valid = validateForm();
        if (!valid) {
            setSubmitting(false);
            return setErrorAlert("Errors exist on the page");
        }

        setSubmitting(false);
        setAuditModalVisible(true);
    }

    const onSubmit = async (auditForm: AuditForm) => {
        setSubmitting(true);
        setSuccessAlert(false);
        setErrorAlert(null);

        const valid = validateForm();
        if (!valid) {
            setSuccessAlert(false);
            setSubmitting(false);
            return setErrorAlert("Errors exist on the page");
        }

        const formState = formApi.getFormState();
        isNew ? await postUser(formState) : await patchUser(formState, auditForm.Annotation);

        setSubmitting(false);
    }

    const postUser = async (formState: FormState) => {
        const creationModel: UserCreation = {
            email: formState.values.email as string,
            firstName: formState.values.firstName as string,
            lastName: formState.values.lastName as string,
            roleIds: (formState.values.roles as any[]).map(r => r.value),
            countryId: (formState.values.country as Country).id as string,
            timeZoneId: (formState.values.timeZone as TimeZone).id as string,
            siteIds: formState.values.sites && (formState.values.sites as any[]).length > 0 ? (formState.values.sites as any[]).map(r => r.value) : [],
            preferredLanguageCode: (formState.values.language as  { code: string, name: string })?.code,
            spirotracUserGroupId: formState.values.spirotracUserGroup as string,
            isRegisteredForMobileApp: formState.values.registeredForMobileApp as boolean
        } 

        try {
            const user = await client.client.postUser(creationModel);
            if (user)
                setSuccessAlert(true);
        } catch (ex) {
            setErrorAlert(ex.message);
            setAuditModalVisible(false);
        }
    }

    const patchUser = async (formState: FormState, auditAnnotation: string) => {
        const mutationModel: UserMutation = {
            roleIds: (formState.values.roles as any[]).map(r => r.value),
            siteIds: formState.values.sites && (formState.values.sites as any[]).length > 0 ? (formState.values.sites as any[]).map(r => r.value) : [],
            countryId: (formState.values.country as Country)?.id,
            timeZoneId: (formState.values.timeZone as TimeZone)?.id,
            preferredLanguageCode: (formState.values.language as { code: string, name: string })?.code,
            spirotracUserGroupId: formState.values.spirotracUserGroup as string,
            isRegisteredForMobileApp: formState.values.registeredForMobileApp as boolean,
            isDisabled: formState.values.isDisabled as boolean,
        }

        try {
            const userResult = await client.client.patchUser(mutationModel, userId, auditAnnotation);
            if (userResult)
                setSuccessAlert(true);
            setAuditModalVisible(false);
        } catch (ex) {
            setErrorAlert(ex.message);
            setAuditModalVisible(false);
        }
    }

    const ActionButtons = ({ submitting, automationIdPrefix, onSaveClick }) => {
        return (
            <div className="h2 pull-right">
                <AsyncActionButton className="btn btn-primary" type="submit" isSubmitting={submitting} disabled={submitting} onClick={onSaveClick} data-automationid={`${automationIdPrefix}_Save`}>
                    Save
                </AsyncActionButton>&nbsp;
                <Link className="btn btn-primary btn-outline" to="/users" data-automationid={`${automationIdPrefix}_ReturnToList`}>
                    Return to List
                </Link>
            </div>
        );
    }

    if (loading)
        return <LoadingWithMessage message="Loading User Form..." />

    return (
        <>
            <Alert show={successAlert} category={AlertCategoryEnum.Success}>
                Saved successfully. Return to{" "}
                <Link to={`/users`}>Users list</Link>?
            </Alert>
            <Alert show={errorAlert !== null} category={AlertCategoryEnum.Danger}>
                {errorAlert}
            </Alert>
            <div className="row">
                <div className="col-md-6">
                    <h2 className="text-primary" data-automationid="CreateUserHeader">{header}</h2>
                </div>
            </div>
            <div className="row">
                <div className="col-md-12">
                    <div id="corePanel" className="panel panel-primary">
                        <div className="panel-heading">
                            <h4>Core Details</h4>
                        </div>
                        <div className="panel-body">
                            <div className="row">
                                <div className="form-group col-sm-6 col-xs-12">
                                    <label htmlFor="Name">Email</label>
                                    <TextBox
                                        name="email"
                                        id="email"
                                        type="email"
                                        field="email"
                                        maxLength={256}
                                        disabled={!isNew}
                                        validate={validateEmail}
                                    />
                                </div>
                                <div className="form-group col-sm-6 col-xs-12">
                                    <label htmlFor="Name">Web Portal Roles</label>
                                    <MultiSelect
                                        name="roles"
                                        id="roles"
                                        field="roles"
                                        labelKey="label"
                                        valueKey="value"
                                        multi
                                        options={roleOptions}
                                        validate={validateRoles}
                                    />
                                </div>
                            </div>
                            <div className="row">
                                <div className="form-group col-sm-6 col-xs-12">
                                    <label htmlFor="Name">First Name</label>
                                    <TextBox
                                        name="firstName"
                                        id="firstName"
                                        field="firstName"
                                        validate={isNew ? Required : undefined}
                                        disabled={!isNew}
                                    />
                                </div>
                                <div className="form-group col-sm-6 col-xs-12">
                                    <label htmlFor="Name">Last Name</label>
                                    <TextBox
                                        name="lastName"
                                        id="lastName"
                                        field="lastName"
                                        validate={isNew ? Required : undefined}
                                        disabled={!isNew}
                                    />
                                </div>
                            </div>
                            <div className="row">
                                <div className="form-group col-md-6 col-sm-6 col-xs-12">
                                    <label htmlFor="Name">Sites</label>
                                    <MultiSelect
                                        name="sites"
                                        id="sites"
                                        field="sites"
                                        labelKey="label"
                                        valueKey="value"
                                        multi
                                        options={siteOptions}
                                        validate={validateSites}
                                    />
                                </div>

                                <div className="form-group col-md-6 col-sm-6 col-xs-12">
                                    <label htmlFor="timeZone">Preferred Time Zone</label>
                                    <MultiSelect
                                        name="timeZone"
                                        id="timeZone"
                                        field="timeZone"
                                        labelKey="name"
                                        valueKey="id"
                                        options={timeZoneOptions}
                                        disabled={!isNew}
                                        validate={isNew ? Required : undefined}
                                    />
                                </div>
                            </div>
                            <div className="row">
                                <div className="form-group col-md-6 col-sm-6 col-xs-12">
                                    <label htmlFor="language">Preferred Language</label>
                                    <MultiSelect
                                        name="language"
                                        id="language"
                                        field="language"
                                        labelKey="name"
                                        valueKey="code"
                                        options={languageOptions}
                                        disabled={!isNew}
                                    />
                                </div>

                                <div className="form-group col-md-6 col-sm-6 col-xs-12">
                                    <label htmlFor="country">Country</label>
                                    <MultiSelect
                                        name="country"
                                        id="country"
                                        field="country"
                                        labelKey="name"
                                        valueKey="id"
                                        options={countries}
                                        validate={isNew ? Required : undefined}
                                        disabled={!isNew}
                                    />
                                </div>
                            </div>
                            <div className="row">
                                {!isNew ? <div className="form-group col-md-6 col-sm-6 col-xs-12">
                                    <Checkbox name="isDisabled" id="isDisabled" field="isDisabled" label="Is Disabled" />
                                </div> : null}
                            </div>
                        </div>
                    </div>
                    <div id="deviceIntegrationPanel" className="panel panel-primary">
                        <div className="panel-heading">
                            <h4>Device Integration</h4>
                        </div>
                        <div className="panel-body">
                            <div className="row">
                                <div className="form-group col-md-6 col-sm-6 col-xs-12">
                                    <label htmlFor="spirotracUserGroup">Spirotrac VI User Group</label>
                                    <Dropdown name="spirotracUserGroup" id="spirotracUserGroup" field="spirotracUserGroup" validate={validateSpirotracUserGroup}>
                                        {spirotracUserGroupOptions.map(cr => (<option key={`${cr.value}`} value={cr.value}>{cr.label}</option>))}
                                    </Dropdown>
                                </div>
                                {
                                    <div className="form-group col-md-6 col-sm-6 col-xs-12">
                                        <Checkbox name="registeredForMobileApp" id="registeredForMobileApp" field="registeredForMobileApp" label="User Is Registered For Mobile Spirometry App" />
                                    </div>
                                }
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="row">
                <div className="col-md-12">
                    <ActionButtons submitting={submitting} onSaveClick={isNew ? onSubmit : onPreAuditSubmit} automationIdPrefix="ActionsFooter" />
                </div>
            </div>
            {auditModalVisible ? (
                <AuditModal
                    header="Approval"
                    isSubmitting={submitting}
                    onClose={() => setAuditModalVisible(false)}
                    onSubmit={(audit) => onSubmit(audit)}
                />
            ) : null}
        </>
    );
}
